import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { GET_TAM_PRIMES } from '@bloomays-lib/adapter.api-talent';
import { errorLogger } from '../helpers/error';
import { useAnnualTamPrimesStore } from '../stores/annualTamPrimesStore';
import type { ActiveTam, AnnualTamPrimeAggregate, CLevelSynthesisRow, GetTamPrimesQueryData } from '../types/primes';
import { buildAnnualTamPrimesCacheKey } from '../utils/annualTamPrimesCache.utils';
import {
  buildTamDisplayName,
  createEmptyMonths,
  createErrorMonths,
  createLoadingMonths,
  isYearConsultable,
  mapAnnualTamPrimeToMonthlySynthesisCells,
} from '../utils/clevelPrimesDashboard.utils';

const buildSuccessRow = (
  tam: ActiveTam,
  aggregate: AnnualTamPrimeAggregate,
  selectedYear: string,
): CLevelSynthesisRow => ({
  rowId: tam.email,
  tamEmail: tam.email,
  tamDisplayName: buildTamDisplayName(tam),
  rowStatus: 'success',
  months: mapAnnualTamPrimeToMonthlySynthesisCells(aggregate, selectedYear),
  annualTamPrime: aggregate,
});

const buildEmptyConsultationRows = (activeTams: ActiveTam[]): CLevelSynthesisRow[] =>
  activeTams.map((tam) => ({
    rowId: tam.email,
    tamEmail: tam.email,
    tamDisplayName: buildTamDisplayName(tam),
    rowStatus: 'success',
    months: createEmptyMonths(),
  }));

export const useCLevelAnnualSynthesis = (activeTams: ActiveTam[], selectedYear: string): CLevelSynthesisRow[] => {
  const client = useApolloClient();
  const [synthesisRows, setSynthesisRows] = useState<CLevelSynthesisRow[]>([]);
  /**
   * Compteur de génération des consultations en cours.
   * Chaque changement d'année (ou de liste TAM) incrémente cette valeur et relance N appels parallèles.
   * Les réponses des générations précédentes sont ignorées pour éviter d'écraser l'état affiché.
   *
   * Scénario évité : l'utilisateur sélectionne 2027, puis 2026 avant la fin des requêtes 2027.
   * Sans ce garde-fou, une réponse 2027 arrivant après la 2026 pourrait remplacer les marges affichées.
   */
  const requestGenerationRef = useRef(0);
  const activeTamsSignature = useMemo(() => activeTams.map((tam) => tam.email).join('|'), [activeTams]);

  useEffect(() => {
    // Identifie cette vague de requêtes ; toute réponse d'une génération antérieure sera ignorée.
    const generation = ++requestGenerationRef.current;

    if (activeTams.length === 0) {
      setSynthesisRows([]);
      return;
    }

    if (!isYearConsultable(selectedYear)) {
      setSynthesisRows(buildEmptyConsultationRows(activeTams));
      return;
    }

    setSynthesisRows(
      activeTams.map((tam) => {
        const cacheKey = buildAnnualTamPrimesCacheKey(tam.email, selectedYear);
        const cachedEntry = useAnnualTamPrimesStore.getState().getCache(cacheKey);

        if (cachedEntry) {
          return buildSuccessRow(tam, cachedEntry.aggregate, selectedYear);
        }

        return {
          rowId: tam.email,
          tamEmail: tam.email,
          tamDisplayName: buildTamDisplayName(tam),
          rowStatus: 'loading' as const,
          months: createLoadingMonths(),
        };
      }),
    );

    activeTams.forEach((tam) => {
      const cacheKey = buildAnnualTamPrimesCacheKey(tam.email, selectedYear);

      client
        .query<GetTamPrimesQueryData>({
          query: GET_TAM_PRIMES,
          variables: {
            tamEmail: tam.email,
            year: selectedYear,
          },
          context: { clientName: 'api.talents' },
          fetchPolicy: 'network-only',
        })
        .then((response) => {
          // Réponse stale : une sélection plus récente (ex. changement 2027 → 2026) a déjà relancé les appels.
          if (generation !== requestGenerationRef.current) {
            return;
          }

          const aggregate = response.data?.getTamPrimes;
          if (!aggregate) {
            setSynthesisRows((previousRows) =>
              previousRows.map((row) =>
                row.tamEmail === tam.email
                  ? {
                      ...row,
                      rowStatus: 'error',
                      months: createErrorMonths(),
                    }
                  : row,
              ),
            );
            return;
          }

          useAnnualTamPrimesStore.getState().setCache(cacheKey, aggregate);
          setSynthesisRows((previousRows) =>
            previousRows.map((row) =>
              row.tamEmail === tam.email ? buildSuccessRow(tam, aggregate, selectedYear) : row,
            ),
          );
        })
        .catch((error: Error) => {
          if (generation !== requestGenerationRef.current) {
            return;
          }

          errorLogger(error, {
            extraInfos: `getTamPrimes failed for ${tam.email} on /primes/dashboard`,
          });
          setSynthesisRows((previousRows) =>
            previousRows.map((row) =>
              row.tamEmail === tam.email
                ? {
                    ...row,
                    rowStatus: 'error',
                    months: createErrorMonths(),
                  }
                : row,
            ),
          );
        });
    });
  }, [activeTams, activeTamsSignature, client, selectedYear]);

  return synthesisRows;
};
